import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TaskAssignment, TaskAssignmentExposureContribution, TaskTimeRange } from 'src/app/models/taskAssignment.model';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { ArrayFormField, ButtonFormField, FormField, LabelFormField, TextFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { TaskAssignmentDialogComponent } from '../task-assignment-dialog/task-assignment-dialog.component';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { TaskAssignmentService } from 'src/app/services/taskAssignment.service';

export interface IMultipleTaskAssignmentFormDialogData extends IFormDialogData {
    taskAssignmentExposure: TaskAssignmentExposureContribution;
    sensorAssignmentId: string;
    sampleStartTime: number;
    sampleEndTime: number;
    taskTimeRanges: TaskTimeRange[];
}

@Component({
  standalone: false,
    selector: 'app-multiple-task-assignment-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class MultipleTaskAssignmentDialog extends FormComponent<TaskAssignmentExposureContribution> implements OnInit {

    constructor(dialogRef: MatDialogRef<MultipleTaskAssignmentDialog>,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: IMultipleTaskAssignmentFormDialogData,
        protected injector: Injector,
        private taskAssignmentService: TaskAssignmentService
    ) {
        super(dialogRef, null, data, "Tasks", injector);
        this.isReadonly = true;
    }
    taskName: string;

    ngOnInit() {
        this.isFormLoading = true;
        this.initModel();
        this.initFields();
        this.isFormLoading = false;
    }

    getNewObject(): TaskAssignmentExposureContribution {
        return new TaskAssignmentExposureContribution();
    }

    initModel(): void {
        this.entity = this.data.taskAssignmentExposure;
        this.taskName = this.entity.taskName;
        this.entity.taskAssignments.forEach((taskAssignment) => {
            taskAssignment.startTime = this.dateFormatService.formatTimestampToString(taskAssignment.startDate, this.dateFormatService.dateToTimeFormat12);
            taskAssignment.endTime = this.dateFormatService.formatTimestampToString(taskAssignment.endDate, this.dateFormatService.dateToTimeFormat12);
        });
    }

    validate(): boolean {
        return true;
    }

    initTaskFields(): FormField[][] {
        return Array.from({ length: this.data.taskAssignmentExposure.taskAssignments.length }, () => this.createTaskFields());
    }

    openTaskDialog(taskAssignment: TaskAssignment) {
        const taskTimeRanges = taskAssignment.id 
        ? this.data.taskTimeRanges.filter(task => task.id !== taskAssignment.id) 
        : this.data.taskTimeRanges;

        const dialogRef = this.dialog.open(TaskAssignmentDialogComponent, {
            data: {
                id: taskAssignment?.id,
                sensorAssignmentId: this.data.sensorAssignmentId,
                sampleStartTime: this.data.sampleStartTime,
                sampleEndTime: this.data.sampleEndTime,
                taskTimeRanges: taskTimeRanges,
            },
            width: '550px',
            disableClose: true
        });

        dialogRef.afterClosed()
            .subscribe((result: any) => {
                if (result) {
                    this.close();
                    this.dialogRef.close(true);
                }
            });
    }

    openTaskDeleteDialog(taskAssignmentId: string) {
        const dialogRef = this.dialog.open(DeleteDialog, {
            data: {
                id: taskAssignmentId,
                formDeletes: true,
                service: this.taskAssignmentService,
                title: "Task"
            },
            width: '440px',
            disableClose: true
        });

        dialogRef.afterClosed()
            .subscribe((result: any) => {
                if (result) {
                    this.close();
                    this.dialogRef.close(true);
                }
            });
    }


    createTaskFields(): FormField[] {
        const taskAssignmentExposureFields = [
            new LabelFormField({
                property: nameOf<TaskAssignmentExposureContribution>(x => x.taskName),
                name: this.taskName,
                columnSpan: new ColumnSpan(1, 5),
                cssClass: "multiple-row",
                required: false,
                readonly: true
            }),
            new TextFormField({
                property: nameOf<TaskAssignment>(x => x.startTime),
                name: "Start Time",
                columnSpan: new ColumnSpan(1, 5),
                cssClass: "multiple-row",
                required: false,
                readonly: true
            }),
            new TextFormField({
                property: nameOf<TaskAssignment>(x => x.endTime),
                name: "End Time",
                columnSpan: new ColumnSpan(1, 5),
                cssClass: "multiple-row",
                required: false,
                readonly: true
            }),
            new ButtonFormField({
                property: nameOf<TaskAssignment>(x => x.taskId),
                name: "",
                columnSpan: new ColumnSpan(1, 5),
                cssClass: "multiple-row",
                iconClass: "fa fa-pen-square big-icon",
                required: false,
                onClick: (taskAssignment: TaskAssignment) => {
                    this.openTaskDialog(taskAssignment);
                }
            }),
            new ButtonFormField({
                property: nameOf<TaskAssignment>(x => x.taskId),
                name: "",
                columnSpan: new ColumnSpan(1, 5),
                cssClass: "multiple-row",
                iconClass: "fa fa-trash big-icon",
                required: false,
                onClick: (taskAssignment: TaskAssignment) => {
                    this.openTaskDeleteDialog(taskAssignment.id);
                }
            }),
        ];
        return taskAssignmentExposureFields;
    }

    onBeforeSave(model: TaskAssignmentExposureContribution): void { }

    initFields() {
        this.fields = [
            new ArrayFormField({
                property: nameOf<TaskAssignmentExposureContribution>(x => x.taskAssignments), name: "",
                items: this.initTaskFields(), cssClass: "multiple-rows", columnSpan: new ColumnSpan(1, 1)
            })
        ];
    }
}
